import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PartnerCommunication {
  id: string;
  partner_id: string;
  communication_type: 'note' | 'call' | 'email' | 'meeting' | 'training';
  title: string;
  content?: string;
  scheduled_date?: string;
  completed_date?: string;
  created_by_staff_id?: string;
  created_at: string;
  updated_at: string;
  staff?: {
    first_name: string;
    last_name: string;
  };
}

export interface CreateCommunicationData {
  partner_id: string;
  communication_type: 'note' | 'call' | 'email' | 'meeting' | 'training';
  title: string;
  content?: string;
  scheduled_date?: string;
}

export const usePartnerCommunications = (partnerId: string) => {
  return useQuery({
    queryKey: ["partner-communications", partnerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_partner_communications')
        .select(`
          *,
          staff:crm_staff(first_name, last_name)
        `)
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching partner communications:", error);
        throw error;
      }
      return data as PartnerCommunication[];
    },
    enabled: !!partnerId
  });
};

export const useCreatePartnerCommunication = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateCommunicationData) => {
      const { data: result, error } = await supabase
        .from('crm_partner_communications')
        .insert(data)
        .select()
        .single();
      
      if (error) {
        throw new Error(error.message || "Failed to create communication");
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["partner-communications", result.partner_id] });
      toast({
        title: "Success",
        description: "Communication logged successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to log communication: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useUpdatePartnerCommunication = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateCommunicationData> }) => {
      const { data: result, error } = await supabase
        .from('crm_partner_communications')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        throw new Error(error.message || "Failed to update communication");
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["partner-communications", result.partner_id] });
      toast({
        title: "Success",
        description: "Communication updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update communication: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};